import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, CircleAlert, Info, Lock, LockOpen } from 'lucide-react';
import type { TenantSummary } from '@forq/contracts';
import type { OrderGateway, OrderRecord } from '@forq/orders-data';
import { CustomizableMetrics, EmptyState, MetricTile, StatusPill, WorkspacePage } from '@forq/ui';
import { FinanceNav } from './FinanceNav';
import { periodLabel } from '@forq/finance-data';
import { dateLabel, errorMessage, money } from '@forq/finance-data';
import { PeriodFilter, useFinancePeriod } from './PeriodFilter';
import {
  buildClosureSnapshot,
  canClosePeriod,
  closurePeriod,
  computeClosingChecks,
  computeClosureDrift,
  hasDrift,
  openCheckCount,
  type ClosingCheckState,
  type PeriodClosingGateway,
  type PeriodClosure,
} from '@forq/finance-data';
import { todayIso, type FinanceGateway, type Invoice } from '@forq/finance-data';
import type { IncomingInvoice, IncomingInvoiceGateway } from '@forq/finance-data';

interface AbschlussPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly userName: string;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly ordersGateway: OrderGateway;
  readonly closingGateway: PeriodClosingGateway;
}

const checkIcons: Readonly<Record<ClosingCheckState, typeof Check>> = {
  ok: Check,
  open: CircleAlert,
  info: Info,
};

export function AbschlussPage({
  tenant, mode, permissions, userName, financeGateway, incomingGateway, ordersGateway, closingGateway,
}: AbschlussPageProps) {
  const navigate = useNavigate();
  const [period, setPeriod] = useFinancePeriod();
  const [invoices, setInvoices] = useState<readonly Invoice[]>([]);
  const [incoming, setIncoming] = useState<readonly IncomingInvoice[]>([]);
  const [orders, setOrders] = useState<readonly OrderRecord[]>([]);
  const [closures, setClosures] = useState<readonly PeriodClosure[]>([]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = todayIso();
  const canClose = permissions.has('finance.invoices.approve');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [invoiceList, incomingList, orderPage, closureList] = await Promise.all([
        financeGateway.listInvoices({ tenantId: tenant.id }),
        incomingGateway.listIncomingInvoices({ tenantId: tenant.id }),
        ordersGateway.searchOrders({ tenantId: tenant.id, bucket: 'all', pageSize: 50 }),
        closingGateway.listClosures(tenant.id),
      ]);
      setInvoices(invoiceList);
      setIncoming(incomingList);
      setOrders(orderPage.items);
      setClosures(closureList);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setLoading(false);
    }
  }, [financeGateway, incomingGateway, ordersGateway, closingGateway, tenant.id]);

  useEffect(() => { void load(); }, [load]);

  const checks = useMemo(
    () => computeClosingChecks(orders, invoices, incoming, period, today),
    [orders, invoices, incoming, period, today],
  );
  const snapshot = useMemo(() => buildClosureSnapshot(invoices, incoming, period), [invoices, incoming, period]);
  const ready = canClosePeriod(checks);
  const openCount = openCheckCount(checks);
  const periodComplete = Boolean(period.from && period.to);

  /** Every closure re-measured against the period as it stands today. */
  const closureRows = useMemo(() => closures.map((closure) => {
    const current = buildClosureSnapshot(invoices, incoming, closurePeriod(closure));
    const drift = computeClosureDrift(closure, current);
    return { closure, drift, drifted: hasDrift(drift) };
  }), [closures, invoices, incoming]);

  const closePeriod = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await closingGateway.closePeriod({ tenantId: tenant.id, period, closedBy: userName, note, snapshot });
      setNote('');
      setClosures(await closingGateway.listClosures(tenant.id));
    } catch (closeError) {
      setError(errorMessage(closeError));
    } finally {
      setBusy(false);
    }
  }, [closingGateway, tenant.id, period, userName, note, snapshot]);

  const reopen = useCallback(async (closure: PeriodClosure) => {
    setBusy(true);
    setError(null);
    try {
      await closingGateway.reopenPeriod(tenant.id, closure.id);
      setClosures(await closingGateway.listClosures(tenant.id));
    } catch (reopenError) {
      setError(errorMessage(reopenError));
    } finally {
      setBusy(false);
    }
  }, [closingGateway, tenant.id]);

  return (
    <WorkspacePage
      customizeKey="finance-abschluss"
      eyebrow={`FORQ Finance · Abschluss · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Periodenabschluss"
      description="Zeitraum prüfen, offene Punkte abarbeiten und die Zahlen für die Übergabe an die Fibu festschreiben."
      metrics={(
        <CustomizableMetrics
          pageKey="finance-abschluss"
          tiles={[
            { key: 'period', node: <MetricTile label="Zeitraum" value={periodComplete ? periodLabel(period) : 'Nicht gewählt'} hint={periodComplete ? 'zum Abschluss' : 'von und bis setzen'} tone={periodComplete ? 'info' : 'warning'} /> },
            { key: 'open', node: <MetricTile label="Offene Punkte" value={openCount} hint={ready ? 'Abschluss möglich' : 'blockieren den Abschluss'} tone={ready ? 'default' : 'danger'} /> },
            { key: 'grossprofit', node: <MetricTile label="Rohertrag im Zeitraum" value={money(snapshot.grossProfit)} hint={`Umsatz ${money(snapshot.netRevenue)}`} tone="default" /> },
            { key: 'closures', node: <MetricTile label="Abgeschlossene Perioden" value={closures.length} hint={closureRows.some((row) => row.drifted) ? 'mit Abweichung!' : 'ohne Abweichung'} tone={closureRows.some((row) => row.drifted) ? 'danger' : 'default'} /> },
          ]}
        />
      )}
      toolbar={<div className="finance-toolbar"><FinanceNav /><PeriodFilter period={period} onChange={setPeriod} /></div>}
    >
      {error && <div className="finance-page__error">{error}</div>}

      <p className="finance-note">
        <Info size={15} aria-hidden />
        <span>
          <strong>Was ein Abschluss hier bedeutet:</strong> FORQ bucht nicht – die Fibu bucht. Der Abschluss
          friert deshalb die Zahlen des Zeitraums ein und meldet, wenn sich danach noch etwas daran ändert.
          Damit fällt auf, wenn ein Beleg nachträglich in einen bereits übergebenen Monat rutscht.
        </span>
      </p>

      {loading ? (
        <p className="finance-work__empty">Abschlussprüfung läuft …</p>
      ) : (
        <>
          <div className="finance-report-grid">
            <section className="finance-report">
              <h3 className="finance-section__title">Prüfliste <small>· {periodLabel(period)}</small></h3>
              <ul className="finance-checks">
                {checks.map((check) => {
                  const Icon = checkIcons[check.state];
                  return (
                    <li key={check.key} className={`finance-checks__item is-${check.state}`}>
                      <span className="finance-checks__icon"><Icon size={14} aria-hidden /></span>
                      <span className="forq-stack">
                        <strong>{check.label}</strong>
                        <small>{check.detail}</small>
                      </span>
                      {check.count > 0 && (
                        <button type="button" className="finance-work__cta" onClick={() => navigate(check.route)}>
                          Ansehen
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="finance-report">
              <h3 className="finance-section__title">Abschluss durchführen</h3>
              <table className="finance-statement">
                <tbody>
                  <tr><th scope="row">Umsatz (netto)</th><td className="forq-cell-num">{money(snapshot.netRevenue)}</td></tr>
                  <tr><th scope="row">Aufwand (netto)</th><td className="forq-cell-num">{money(snapshot.expense)}</td></tr>
                  <tr><th scope="row">USt-Zahllast</th><td className="forq-cell-num">{money(snapshot.vatPayable)}</td></tr>
                  <tr><th scope="row">Belege</th><td className="forq-cell-num">{snapshot.outgoingCount} / {snapshot.incomingCount}</td></tr>
                  <tr className="is-sum"><th scope="row">Rohertrag</th><td className="forq-cell-num">{money(snapshot.grossProfit)}</td></tr>
                </tbody>
              </table>

              <label className="finance-closing__note">
                <span>Notiz zum Abschluss (optional)</span>
                <input
                  type="text"
                  value={note}
                  placeholder="z. B. an Steuerberater übergeben am …"
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>

              {!periodComplete && <p className="finance-source">Bitte oben einen Zeitraum mit Beginn und Ende wählen.</p>}
              {periodComplete && !ready && (
                <p className="finance-source">{openCount} {openCount === 1 ? 'Punkt' : 'Punkte'} aus der Prüfliste {openCount === 1 ? 'blockiert' : 'blockieren'} den Abschluss.</p>
              )}
              {!canClose && <p className="finance-source">Für den Abschluss fehlt die Berechtigung zur Rechnungsfreigabe.</p>}

              <button
                type="button"
                className="finance-actions__btn is-pay finance-closing__submit"
                disabled={!periodComplete || !ready || !canClose || busy}
                onClick={() => void closePeriod()}
              >
                <Lock size={15} /> Periode abschließen
              </button>
            </section>
          </div>

          <section className="finance-report">
            <h3 className="finance-section__title">Abgeschlossene Perioden</h3>
            {closureRows.length === 0 ? (
              <EmptyState
                eyebrow="Abschluss"
                title="Noch keine Periode abgeschlossen"
                description="Sobald ein Zeitraum abgeschlossen ist, stehen hier die eingefrorenen Zahlen und jede spätere Abweichung."
              />
            ) : (
              <div className="finance-closures">
                {closureRows.map(({ closure, drift, drifted }) => (
                  <article key={closure.id} className={`finance-closure${drifted ? ' is-drifted' : ''}`}>
                    <header>
                      <span className="forq-stack">
                        <strong>{periodLabel(closurePeriod(closure))}</strong>
                        <small>
                          abgeschlossen am {dateLabel(closure.closedAt.slice(0, 10))} von {closure.closedBy}
                          {closure.note && ` · ${closure.note}`}
                        </small>
                      </span>
                      {drifted
                        ? <StatusPill label="Abweichung" tone="danger" />
                        : <StatusPill label="Unverändert" tone="done" />}
                      <button type="button" className="finance-actions__btn is-danger" disabled={busy} onClick={() => void reopen(closure)}>
                        <LockOpen size={13} /> Wieder öffnen
                      </button>
                    </header>

                    {drifted && (
                      <>
                        <p className="finance-closure__warn">
                          <AlertTriangle size={14} aria-hidden />
                          Nach dem Abschluss hat sich der Zeitraum verändert. Die Fibu hat die eingefrorenen Werte bekommen.
                        </p>
                        <table className="finance-statement">
                          <thead>
                            <tr><th scope="col">Kennzahl</th><th scope="col">Eingefroren</th><th scope="col">Jetzt</th><th scope="col">Abweichung</th></tr>
                          </thead>
                          <tbody>
                            {drift.filter((row) => row.delta !== 0).map((row) => (
                              <tr key={row.label}>
                                <th scope="row">{row.label}</th>
                                <td className="forq-cell-num">{money(row.frozen)}</td>
                                <td className="forq-cell-num">{money(row.current)}</td>
                                <td className="forq-cell-num finance-overdue">{row.delta > 0 ? '+' : ''}{money(row.delta)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </WorkspacePage>
  );
}
